import React from 'react';
import { useParams } from 'react-router-dom';
import { ArrowUp, ArrowDown } from 'lucide-react';
import { SortBy } from '../../../../types';
import { useUserTags } from '../../../../hooks/useTags';
import { TableHead, TableRow } from '../../../../components/ui/table';
import { Button } from '../../../../components/ui/button';

const FileListHeader = () => {
  const { folderId } = useParams<{ folderId: string }>();
  const isInRecentsView = folderId === 'recents';
  const { getTag, setTag } = useUserTags();
  const sortBy = (getTag('library-sort-by') as SortBy) || 'createdAt';
  const sortOrder = (getTag('library-sort-order') as 'asc' | 'desc') || 'desc';

  const handleSort = (column: SortBy) => {
    if (sortBy === column) {
      setTag({
        key: 'library-sort-order',
        value: sortOrder === 'asc' ? 'desc' : 'asc',
      });
    } else {
      setTag({ key: 'library-sort-by', value: column });
      setTag({
        key: 'library-sort-order',
        value: column === 'name' ? 'asc' : 'desc',
      });
    }
  };

  // Component for sortable headers
  const SortableHeader = ({
    column,
    label,
    className = '',
  }: {
    column: SortBy;
    label: string;
    className?: string;
  }) => (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => handleSort(column)}
      className={`h-auto px-2 py-1 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-transparent transition-colors ${className}`}
    >
      {label}
      {sortBy === column &&
        (sortOrder === 'asc' ? (
          <ArrowUp className="ml-1 h-3 w-3" />
        ) : (
          <ArrowDown className="ml-1 h-3 w-3" />
        ))}
    </Button>
  );

  // Component for non-sortable headers
  const HeaderText = ({
    label,
    className = '',
  }: {
    label: string;
    className?: string;
  }) => (
    <span className={`text-xs font-medium text-muted-foreground ${className}`}>
      {label}
    </span>
  );

  return (
    <TableRow className="border-b hover:bg-transparent">
      <TableHead className="h-10 w-[45%] min-w-[250px] pl-4">
        <div className="flex items-center gap-3">
          <div className="w-8 flex-shrink-0" />
          <SortableHeader column="name" label="Name" className="-ml-2" />
        </div>
      </TableHead>

      <TableHead className="h-10 w-[70px] text-center">
        <SortableHeader column="duration" label="Duration" />
      </TableHead>

      {isInRecentsView && (
        <TableHead className="h-10 w-[100px] hidden sm:table-cell text-center">
          <HeaderText label="Folder" />
        </TableHead>
      )}

      <TableHead className="h-10 w-[80px] hidden md:table-cell text-center">
        <HeaderText label="Creator" />
      </TableHead>

      <TableHead className="h-10 w-[60px] text-center">
        <SortableHeader column="createdAt" label="Date" />
      </TableHead>

      <TableHead className="h-10 w-[44px]" />
    </TableRow>
  );
};

export default FileListHeader;
